import React, { useState, useEffect } from "react";
import { Post } from "@/core/types/postTypes";
import { apiClient } from "@/services/apiClient";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { ErrorIcon, CloseIcon } from "@/components/icons";

interface ManualCaptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: Post;
  clientId: string;
  initialCaption?: string;
  onCaptionSaved: (updatedPost?: any) => void;
}

export const ManualCaptionModal: React.FC<ManualCaptionModalProps> = ({
  isOpen,
  onClose,
  post,
  clientId,
  initialCaption = "",
  onCaptionSaved,
}) => {
  const [caption, setCaption] = useState(initialCaption);
  const [altText, setAltText] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCaption(initialCaption || post.caption || "");
      setAltText("");
      setKeywords([]);
      setKeywordInput("");
      setError(null);
    }
  }, [isOpen, initialCaption, post.caption]);

  const handleAddKeyword = () => {
    if (keywordInput.trim() && keywords.length < 20) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index: number) => {
    setKeywords(keywords.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleSave = async () => {
    if (!caption.trim()) {
      setError("Caption cannot be empty");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const response = await apiClient.put(
        `/api/client/${clientId}/posts/${post.id}/caption/manual`,
        { caption: caption.trim() }
      );

      if (response.success) {
        onCaptionSaved(response.post);
        onClose();
      }
    } catch (err: any) {
      setError(err.message || "Failed to save caption");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  const characterCount = caption.length;
  const maxLength = 2200;
  const isOverLimit = characterCount > maxLength;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={initialCaption || post.caption ? "Edit Caption" : "Write Caption"}
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!caption.trim() || isOverLimit || isSaving}
            isLoading={isSaving}
          >
            Save Caption
          </Button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        <div className="form-group">
          <label
            htmlFor="caption-text"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Caption *
            <span className="block text-xs text-gray-500 font-normal">
              Write your caption for this post
            </span>
          </label>
          <textarea
            id="caption-text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write your caption here..."
            rows={8}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
              isOverLimit ? "border-red-500" : "border-gray-300"
            }`}
            autoFocus
          />
          <div className="flex items-center justify-between mt-1">
            <span
              className={`text-xs ${
                isOverLimit
                  ? "text-red-600 font-medium"
                  : characterCount > maxLength * 0.9
                    ? "text-yellow-600"
                    : "text-gray-500"
              }`}
            >
              {characterCount}/{maxLength}
            </span>
            {isOverLimit && (
              <span className="text-xs text-red-600">
                Caption exceeds maximum length
              </span>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Keywords
            <span className="block text-xs text-gray-500 font-normal">
              Add SEO keywords for this post (max 20)
            </span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add keyword..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleAddKeyword}
              disabled={!keywordInput.trim() || keywords.length >= 20}
            >
              Add
            </Button>
          </div>
          {keywords.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {keywords.map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-sm text-sm"
                >
                  {keyword}
                  <button
                    onClick={() => handleRemoveKeyword(index)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <CloseIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="form-group">
          <label
            htmlFor="alt-text"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Alt Text
            <span className="block text-xs text-gray-500 font-normal">
              Optional accessibility description (max 125 characters)
            </span>
          </label>
          <input
            id="alt-text"
            type="text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="Brief description for accessibility..."
            maxLength={125}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {altText.length}/125
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-2">
              <ErrorIcon className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <div className="text-red-700 text-sm">
                <div className="font-medium">Error</div>
                <div className="text-red-600">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
